import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/constants";
import { toast } from "sonner";

function GoogleButton({ role }) {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (role) sessionStorage.setItem("ys_signup_role", role);
    const redirectUrl = window.location.origin + "/browse";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" onClick={start} variant="outline" data-testid="google-auth-btn"
      className="w-full rounded-full h-12 gap-2 border-slate-300">
      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
      Continue with Google
    </Button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || "friend"}!`);
      navigate(u.role === "admin" ? "/admin" : "/browse");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white px-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 ys-fade-up">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <img src={LOGO_URL} alt="Yard-skip" className="w-11 h-11 rounded-xl" />
          <span className="font-display font-extrabold text-xl">Yard-<span className="text-ys-sky">skip</span></span>
        </Link>
        <h1 className="font-display text-2xl font-bold text-center">Welcome back</h1>
        <p className="text-slate-500 text-sm text-center mt-1">Sign in to browse, post, and manage sales.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input" className="mt-1.5 rounded-xl h-12" placeholder="you@example.com" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/forgot-password" className="text-xs font-semibold text-ys-sky hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input" className="mt-1.5 rounded-xl h-12" placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="login-submit-btn"
            className="w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700 text-base">
            {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400 font-mono">OR</span><div className="h-px bg-slate-200 flex-1" />
        </div>
        <GoogleButton />
        <p className="text-sm text-center text-slate-500 mt-6">
          New here? <Link to="/register" className="text-ys-sky font-semibold" data-testid="go-register-link">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
